import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "./ui";
import type { Achievement } from "../../types";

/**
 * Centre-screen celebration / encouragement pop-ups, shown one at a time.
 * The parent owns the queue (`items`) and removes a card via `onDismiss(id)`.
 * Portaled to <body> so a `.glass` (backdrop-filter) ancestor can't trap the
 * fixed overlay. Auto-dismisses after a few seconds; tap anywhere to close.
 */
const VARIANT: Record<Achievement["variant"], { icon: string; ring: string; ink: string; emoji: string }> = {
  success: { icon: "trophy", ring: "linear-gradient(160deg,#fcd34d,#f59e0b)", ink: "#7c2d12", emoji: "🎉" },
  retry: { icon: "rotate", ring: "linear-gradient(160deg,#fdba74,#fb923c)", ink: "#7c2d12", emoji: "💪" },
  info: { icon: "info", ring: "linear-gradient(160deg,#c4b5fd,#a78bfa)", ink: "#312e81", emoji: "✨" },
};

const DURATION = 4500;

export default function AchievementOverlay({
  items, onDismiss, theme,
}: { items: Achievement[]; onDismiss: (id: string) => void; theme: "dark" | "light" }) {
  const { t } = useTranslation();
  const current = items[0];
  const [shown, setShown] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const close = (id: string) => {
    if (timer.current) clearTimeout(timer.current);
    setShown(false);
    window.setTimeout(() => onDismiss(id), 200);
  };

  useEffect(() => {
    if (!current) return;
    setShown(false);
    const raf = requestAnimationFrame(() => setShown(true));
    timer.current = window.setTimeout(() => close(current.id), DURATION);
    return () => { cancelAnimationFrame(raf); if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  if (!current) return null;
  const v = VARIANT[current.variant];

  return createPortal(
    // Portaled outside `.beeapp`, so re-establish the theme scope (display:contents
    // adds no box) — otherwise the card's `.glass`/text vars fall back and the
    // text turns black in dark mode.
    <div className="beeapp" data-theme={theme} style={{ display: "contents" }}>
    <div
      onClick={() => close(current.id)}
      style={{
        position: "fixed", inset: 0, zIndex: 5000, display: "grid", placeItems: "center", padding: 24,
        background: "rgba(0,0,0,0.5)", opacity: shown ? 1 : 0, transition: "opacity 0.2s ease",
      }}
    >
      <div
        className="glass glass-lg glass-sheen"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 322, padding: "28px 22px 0", textAlign: "center", overflow: "hidden",
          transform: shown ? "scale(1)" : "scale(0.82)",
          transition: "transform 0.3s cubic-bezier(.2,1.35,.4,1)",
        }}
      >
        <div style={{
          width: 74, height: 74, borderRadius: 22, margin: "0 auto 16px", display: "grid", placeItems: "center",
          background: v.ring, color: v.ink, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.55), 0 8px 24px rgba(0,0,0,0.18)",
        }}>
          <Icon name={v.icon} size={34} stroke={2.2} />
        </div>
        <div className="t-h" style={{ marginBottom: 6, fontSize: "1.2rem" }}>{current.title} {v.emoji}</div>
        {current.message && (
          <div className="t-sm muted" style={{ fontWeight: 600, lineHeight: 1.5 }}>{current.message}</div>
        )}
        {current.note && (
          <div className="t-sm" style={{ marginTop: 10, fontStyle: "italic", opacity: 0.85, lineHeight: 1.5 }}>“{current.note}”</div>
        )}
        <button className="btn btn-violet btn-block" onClick={() => close(current.id)} style={{ margin: "20px 0" }}>
          {t("achv.gotIt")}
        </button>
        <div style={{ height: 3, margin: "0 -22px", background: "var(--glass-1)" }}>
          <div style={{ height: "100%", background: v.ring, width: shown ? "0%" : "100%", transition: `width ${DURATION}ms linear` }} />
        </div>
      </div>
    </div>
    </div>,
    document.body,
  );
}
